import Accordion from "@/components/Accordion";
import Slider from "@/components/Slider";
import ColorPicker from "@/components/ColorPicker";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@/components/Toggle";
import React from "react";
import { ChatWindow } from "../../types";
import LogoUpload from "../../LogoUpload";
import DesignerActionLink from "../../DesignerActionLink";
import { LauncherCustomization } from "./LauncherCustomization";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
  setStagedLauncherIconFile?: (file: File | null) => void;
}

type LauncherPosition = "Left" | "Right";
type LauncherVerticalPosition = "top" | "middle" | "bottom";

const positions: LauncherPosition[] = ["Left", "Right"];

// 3×3 placement grid — center is disabled (launcher cannot dock to center).
type GridCell = {
  h: LauncherPosition;
  v: LauncherVerticalPosition;
  label: string;
  disabled?: boolean;
};
const PLACEMENT_GRID: GridCell[] = [
  { h: "Left", v: "top", label: "Top Left" },
  { h: "Right", v: "top", label: "Top Center", disabled: true },
  { h: "Right", v: "top", label: "Top Right" },
  { h: "Left", v: "middle", label: "Middle Left" },
  { h: "Right", v: "middle", label: "Center", disabled: true },
  { h: "Right", v: "middle", label: "Middle Right" },
  { h: "Left", v: "bottom", label: "Bottom Left" },
  { h: "Right", v: "bottom", label: "Bottom Center", disabled: true },
  { h: "Right", v: "bottom", label: "Bottom Right" }
];

const shadowOptions = [
  { value: "none", label: "None" },
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
  { value: "xl", label: "Extra Large" }
];

const launcherSizes = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" }
];

// Hardcoded defaults used by the Layout "Reset" link.
const LAYOUT_DEFAULTS: Partial<StyleConfigModel> = {
  launcherPosition: "Right",
  sideSpacing: 20,
  bottomSpacing: 20,
  windowWidth: 370,
  windowHeight: 0,
  windowBorderRadius: 32,
  windowBorderWidth: 0,
  windowBorderColor: "#e2e8f0",
  windowShadow: "xl",
  launcherSize: "md",
  launcherText: "",
  openAnimation: "slideUp",
  chatBackdrop: "none",
  scrollToBottomStyle: "arrow",
  inputFieldStyle: "box",
  sendButtonStyle: "icon",
  showUnreadCount: true,
  forceMinimizedDesktop: false,
  forceMinimizedMobile: false,
  fullscreenMobile: false,
  launcherVerticalPosition: "bottom",
  launcherOffsetX: 20,
  launcherOffsetY: 20,
  chatAnchorSide: "auto",
  mobileFullscreen: true,
  mobileLargeButtons: false,
  mobileBreakpointPx: 640
};

// WCAG-and-UX-friendly curated values. Recommended != defaults.
// Source: WCAG 2.5.8 (44px target), WCAG 1.4.3 (4.5:1 contrast — colors handled in Branding),
// 16px input font (prevents iOS zoom), bottom-right placement (heatmap convention).
const LAYOUT_RECOMMENDED: Partial<StyleConfigModel> = {
  launcherPosition: "Right",
  launcherVerticalPosition: "bottom",
  launcherOffsetX: 24,
  launcherOffsetY: 24,
  sideSpacing: 24,
  bottomSpacing: 24,
  chatAnchorSide: "auto",
  windowWidth: 380,
  windowHeight: 0,
  windowBorderRadius: 12,
  windowBorderWidth: 0,
  windowShadow: "lg",
  launcherSize: "md",
  openAnimation: "slideUp",
  chatBackdrop: "none",
  scrollToBottomStyle: "arrow",
  inputFieldStyle: "box",
  sendButtonStyle: "icon",
  showUnreadCount: true,
  forceMinimizedDesktop: true,
  forceMinimizedMobile: true,
  fullscreenMobile: false,
  mobileFullscreen: true,
  mobileLargeButtons: true,
  mobileBreakpointPx: 640
};

export function Layout(props: Props) {
  const workingStyleConfig = props.workingChatWindow.styleConfig || {};
  const linkBubbleToLauncher = workingStyleConfig.linkBubbleToLauncher ?? true;

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      styleConfig: {
        ...workingStyleConfig,
        ...updates
      }
    });
  };

  const setLauncherPosition = (position: LauncherPosition) => {
    const updates: Partial<StyleConfigModel> = { launcherPosition: position };
    if (linkBubbleToLauncher) {
      updates.visitorBubbleAlignment = position === "Left" ? "left" : "right";
    }
    updateStyleConfig(updates);
  };

  const setPlacement = (h: LauncherPosition, v: LauncherVerticalPosition) => {
    const updates: Partial<StyleConfigModel> = {
      launcherPosition: h,
      launcherVerticalPosition: v
    };
    if (linkBubbleToLauncher) {
      updates.visitorBubbleAlignment = h === "Left" ? "left" : "right";
    }
    updateStyleConfig(updates);
  };

  const currentVertical: LauncherVerticalPosition =
    workingStyleConfig.launcherVerticalPosition ?? "bottom";
  const currentHorizontal: LauncherPosition =
    workingStyleConfig.launcherPosition ?? "Right";
  const currentAnchor: "auto" | "left" | "right" =
    workingStyleConfig.chatAnchorSide ?? "auto";

  // Escape-key handler: blur any focused field / close native color pickers
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const active = document.activeElement as HTMLElement | null;
        if (active && typeof active.blur === "function") {
          active.blur();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function clampedNumber(
    e: React.ChangeEvent<HTMLInputElement>,
    min: number,
    max: number,
    fallback: number
  ) {
    let value = parseInt(e.target.value);
    if (isNaN(value)) value = fallback;
    if (value > max) value = max;
    if (value < min) value = min;
    return value;
  }

  const wcagEnabled = workingStyleConfig.enforceWcagAa ?? true;
  const launcherSizeTooSmall = (workingStyleConfig.launcherSize ?? "md") === "sm";
  const windowWidthTooSmall = (workingStyleConfig.windowWidth ?? 370) < 320;
  const windowHeight = workingStyleConfig.windowHeight ?? 0;
  const windowHeightTooSmall = windowHeight > 0 && windowHeight < 400;

  return (
    <Accordion.Item eventKey="layout" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Layout</Accordion.Title>
        <Accordion.SubTitle>
          Position, size, border, and shadow of your chat window
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="space-y-5">
          <div className="flex justify-end gap-3">
            <DesignerActionLink
              ariaLabel="Apply recommended layout values"
              onClick={() => {
                if (
                  !window.confirm(
                    "Apply recommended (WCAG + UX-friendly) layout values? Existing layout settings will be replaced."
                  )
                )
                  return;
                updateStyleConfig(LAYOUT_RECOMMENDED);
              }}
            >
              Recommended
            </DesignerActionLink>
            <DesignerActionLink
              ariaLabel="Reset layout section to defaults"
              onClick={() => {
                if (!window.confirm("Reset layout fields to their defaults?")) return;
                updateStyleConfig(LAYOUT_DEFAULTS);
              }}
            >
              Reset section
            </DesignerActionLink>
          </div>
          {/* Window Position */}
          <div>
            <div className="text-sm font-medium mb-1">Launcher Placement</div>
            <div className="text-xs text-slate-400 mb-2">Pick where the launcher docks on the page. Center positions are not available.</div>
            <div
              className="grid grid-cols-3 gap-1.5 max-w-[220px]"
              role="radiogroup"
              aria-label="Launcher placement"
            >
              {PLACEMENT_GRID.map((cell, idx) => {
                const isSelected =
                  !cell.disabled &&
                  currentHorizontal === cell.h &&
                  currentVertical === cell.v;
                return (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={cell.label}
                    key={idx}
                    disabled={cell.disabled}
                    className={`aspect-square rounded-md border-2 flex items-center justify-center relative min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                      cell.disabled
                        ? "border-slate-100 bg-slate-50 cursor-not-allowed opacity-50"
                        : isSelected
                        ? "border-primary bg-primary-bg cursor-pointer"
                        : "border-slate-300 bg-white hover:border-slate-400 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (cell.disabled) return;
                      setPlacement(cell.h, cell.v);
                    }}
                    title={cell.label}
                  >
                    {!cell.disabled && (
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isSelected ? "bg-[var(--color-primary)]" : "bg-slate-400"
                        }`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between items-center mt-3">
              <div>
                <div className="text-sm font-normal">Link bubble alignment to launcher position</div>
                <div className="text-xs text-slate-400">When ON, visitor bubbles move to the same side as the launcher</div>
              </div>
              <Toggle
                toggled={linkBubbleToLauncher}
                changeToggle={() => {
                  const next = !linkBubbleToLauncher;
                  const updates: Partial<StyleConfigModel> = { linkBubbleToLauncher: next };
                  if (next) {
                    updates.visitorBubbleAlignment =
                      workingStyleConfig.launcherPosition === "Left" ? "left" : "right";
                  }
                  updateStyleConfig(updates);
                }}
              />
            </div>
          </div>

          {/* Launcher Offsets */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium mb-1">Offset X</div>
              <div className="text-xs text-slate-400 mb-2">From the chosen left/right edge</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  aria-label="Launcher horizontal offset in pixels"
                  className="border border-slate-300 rounded-md p-2 w-20 text-sm"
                  value={workingStyleConfig.launcherOffsetX ?? workingStyleConfig.sideSpacing ?? 20}
                  onChange={(e) => {
                    const v = clampedNumber(e, 0, 200, 20);
                    updateStyleConfig({ launcherOffsetX: v, sideSpacing: v });
                  }}
                  min={0}
                  max={200}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Offset Y</div>
              <div className="text-xs text-slate-400 mb-2">From the chosen top/bottom anchor</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  aria-label="Launcher vertical offset in pixels"
                  className="border border-slate-300 rounded-md p-2 w-20 text-sm"
                  value={workingStyleConfig.launcherOffsetY ?? workingStyleConfig.bottomSpacing ?? 20}
                  onChange={(e) => {
                    const v = clampedNumber(e, 0, 200, 20);
                    updateStyleConfig({ launcherOffsetY: v, bottomSpacing: v });
                  }}
                  min={0}
                  max={200}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
          </div>
          <div className="text-[11px] text-slate-400 -mt-2">
            launcherOffsetX/Y take precedence over the legacy sideSpacing/bottomSpacing fields when both are present.
          </div>

          {/* Chat Anchor Side override */}
          <div>
            <div className="text-sm font-medium mb-1">Chat Window Anchor</div>
            <div className="text-xs text-slate-400 mb-2">
              Auto: chat opens on the opposite side of the launcher. Force Left/Right to override.
            </div>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Chat anchor side">
              {(
                [
                  { value: "auto", label: "Auto" },
                  { value: "left", label: "Left" },
                  { value: "right", label: "Right" }
                ] as { value: "auto" | "left" | "right"; label: string }[]
              ).map((opt) => {
                const isSelected = currentAnchor === opt.value;
                return (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ chatAnchorSide: opt.value })}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Window Width */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Window Width</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowWidth ?? 370}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowWidth: clampedNumber(e, 320, 500, 370)
                    })
                  }
                  min={320}
                  max={500}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={500}
              min={320}
              onChange={(value) => updateStyleConfig({ windowWidth: value })}
              value={workingStyleConfig.windowWidth ?? 370}
            />
          </div>

          {/* Window Height */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Window Height</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowHeight ?? 0}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowHeight: clampedNumber(e, 0, 800, 0)
                    })
                  }
                  min={0}
                  max={800}
                  placeholder="Auto"
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={800}
              min={0}
              onChange={(value) => updateStyleConfig({ windowHeight: value })}
              value={workingStyleConfig.windowHeight ?? 0}
            />
            <div className="text-xs text-slate-400 mt-1">Set to 0 for auto height</div>
            {wcagEnabled && (windowWidthTooSmall || windowHeightTooSmall) && (
              <div
                role="note"
                className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-2 mt-2"
              >
                Very small chat windows may be hard to use on mobile or for
                users who need larger text.
              </div>
            )}
          </div>

          {/* Border Radius */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Corner Radius</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowBorderRadius ?? 32}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowBorderRadius: clampedNumber(e, 0, 32, 32)
                    })
                  }
                  min={0}
                  max={32}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={32}
              min={0}
              onChange={(value) =>
                updateStyleConfig({ windowBorderRadius: value })
              }
              value={workingStyleConfig.windowBorderRadius ?? 32}
            />
          </div>

          <div className="border-t border-slate-200" />

          {/* Border */}
          <div>
            <div className="text-sm font-medium mb-2">Border</div>
            <div className="flex items-center gap-4 mb-3">
              <div className="text-sm font-normal w-20">Width</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowBorderWidth ?? 0}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowBorderWidth: clampedNumber(e, 0, 5, 0)
                    })
                  }
                  min={0}
                  max={5}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            {(workingStyleConfig.windowBorderWidth ?? 0) > 0 && (
              <div className="flex items-center gap-4">
                <div className="text-sm font-normal w-20">Color</div>
                <ColorPicker
                  value={workingStyleConfig.windowBorderColor ?? "#e2e8f0"}
                  onChange={(value) =>
                    updateStyleConfig({ windowBorderColor: value })
                  }
                />
              </div>
            )}
          </div>

          {/* Shadow */}
          <div>
            <div className="text-sm font-medium mb-2">Shadow</div>
            <div className="flex flex-wrap gap-2">
              {shadowOptions.map((opt) => {
                const isSelected =
                  (workingStyleConfig.windowShadow ?? "xl") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ windowShadow: opt.value })
                    }
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Launcher Size */}
          <div>
            <div className="text-sm font-medium mb-2">Launcher Size</div>
            {wcagEnabled && launcherSizeTooSmall && (
              <div
                role="note"
                className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-2 mb-2"
              >
                Launcher size below 44 × 44 px may fail WCAG 2.5.8 Target Size.
              </div>
            )}
            <div className="flex gap-3">
              {launcherSizes.map((size) => {
                const isSelected =
                  (workingStyleConfig.launcherSize ?? "md") === size.value;
                return (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={`Launcher size ${size.label}`}
                    key={size.value}
                    className={`flex items-center justify-center rounded-lg py-2 px-5 cursor-pointer border-2 text-sm min-h-[40px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ launcherSize: size.value })
                    }
                  >
                    {size.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Launcher Appearance */}
          <div className="space-y-3">
            <div className="text-sm font-medium">Launcher Appearance</div>

            {/* State-based colors */}
            {[
              {
                label: "Available",
                bgField: "launcherAvailableBgColor" as keyof typeof workingStyleConfig,
                textField: "launcherAvailableTextColor" as keyof typeof workingStyleConfig,
                defaultBg: workingStyleConfig.launcherBgColor ?? "#1670E7",
                defaultText: workingStyleConfig.launcherTextColor ?? "#FFFFFF",
              },
              {
                label: "Unavailable / Offline",
                bgField: "launcherUnavailableBgColor" as keyof typeof workingStyleConfig,
                textField: "launcherUnavailableTextColor" as keyof typeof workingStyleConfig,
                defaultBg: "#9CA3AF",
                defaultText: "#FFFFFF",
              },
              {
                label: "Chatting",
                bgField: "launcherChattingBgColor" as keyof typeof workingStyleConfig,
                textField: "launcherChattingTextColor" as keyof typeof workingStyleConfig,
                defaultBg: workingStyleConfig.launcherBgColor ?? "#1670E7",
                defaultText: workingStyleConfig.launcherTextColor ?? "#FFFFFF",
              },
            ].map((state) => (
              <div key={state.label}>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{state.label}</div>
                <div className="grid grid-cols-3 items-center gap-4 mb-2">
                  <div className="text-sm font-normal">Background</div>
                  <ColorPicker
                    value={(workingStyleConfig[state.bgField] as string) ?? state.defaultBg}
                    onChange={(value) => updateStyleConfig({ [state.bgField]: value })}
                  />
                  <div />
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <div className="text-sm font-normal">Icon Color</div>
                  <ColorPicker
                    value={(workingStyleConfig[state.textField] as string) ?? state.defaultText}
                    onChange={(value) => updateStyleConfig({ [state.textField]: value })}
                  />
                  <div />
                </div>
              </div>
            ))}

            <div>
              <div className="text-sm font-normal mb-1">Launcher Label</div>
              <div className="text-xs text-slate-400 mb-1">Optional text shown next to the chat button, e.g. "Chat with us"</div>
              <input
                type="text"
                aria-label="Launcher Label"
                className="border border-slate-300 rounded-md p-2 w-full text-sm"
                placeholder="Optional text next to launcher icon"
                value={workingStyleConfig.launcherText ?? ""}
                onChange={(e) => updateStyleConfig({ launcherText: e.target.value })}
              />
            </div>
            <LauncherCustomization
              workingStyleConfig={workingStyleConfig}
              updateStyleConfig={updateStyleConfig}
              setStagedLauncherIconFile={props.setStagedLauncherIconFile}
            />
          </div>

          <div className="border-t border-slate-200" />

          {/* Open animation */}
          <div>
            <div className="text-sm font-medium mb-2">Open Animation</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "slideUp", label: "Slide up" },
                { value: "fade", label: "Fade in" },
                { value: "pop", label: "Pop" },
                { value: "none", label: "None" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.openAnimation ?? "slideUp") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ openAnimation: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Page backdrop */}
          <div>
            <div className="text-sm font-medium mb-1">Page Backdrop</div>
            <div className="text-xs text-slate-400 mb-2">Effect applied to the page behind the chat window when it is open</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "none", label: "None" },
                { value: "dim", label: "Dim" },
                { value: "blur", label: "Blur" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.chatBackdrop ?? "none") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ chatBackdrop: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Scroll to bottom */}
          <div>
            <div className="text-sm font-medium mb-1">Scroll-to-Bottom Indicator</div>
            <div className="text-xs text-slate-400 mb-2">Button or badge shown when the visitor has scrolled up in the conversation — click it to jump back to the latest message</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "none", label: "None" },
                { value: "arrow", label: "Arrow button" },
                { value: "badge", label: "New messages badge" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.scrollToBottomStyle ?? "arrow") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ scrollToBottomStyle: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Input & send button */}
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium mb-1">Input Field Style</div>
              <div className="text-xs text-slate-400 mb-2">Controls how the visitor's message text box looks at the bottom of the chat window</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "box", label: "Box" },
                  { value: "underline", label: "Underline" },
                  { value: "pill", label: "Pill" },
                ].map((opt) => {
                  const isSelected = (workingStyleConfig.inputFieldStyle ?? "box") === opt.value;
                  return (
                    <div
                      key={opt.value}
                      className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                        isSelected
                          ? "border-primary bg-primary-bg text-primary-active"
                          : "border-slate-300 bg-white text-slate-600"
                      }`}
                      onClick={() => updateStyleConfig({ inputFieldStyle: opt.value })}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-2">Send Button</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "icon", label: "Icon only" },
                  { value: "text", label: "Text only" },
                  { value: "both", label: "Icon + Text" },
                ].map((opt) => {
                  const isSelected = (workingStyleConfig.sendButtonStyle ?? "icon") === opt.value;
                  return (
                    <div
                      key={opt.value}
                      className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                        isSelected
                          ? "border-primary bg-primary-bg text-primary-active"
                          : "border-slate-300 bg-white text-slate-600"
                      }`}
                      onClick={() => updateStyleConfig({ sendButtonStyle: opt.value })}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Behavior toggles */}
          <div>
            <div className="text-sm font-medium mb-3">Behavior</div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Show unread message count</div>
                  <div className="text-xs text-slate-400">Badge on launcher when messages are unread</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.showUnreadCount ?? true}
                  changeToggle={() =>
                    updateStyleConfig({ showUnreadCount: !(workingStyleConfig.showUnreadCount ?? true) })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Start minimized on desktop</div>
                  <div className="text-xs text-slate-400">When ON: visitors see only the launcher button when the page loads — they must click it to open chat. When OFF: chat opens automatically.</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.forceMinimizedDesktop ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ forceMinimizedDesktop: !workingStyleConfig.forceMinimizedDesktop })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Start minimized on mobile</div>
                  <div className="text-xs text-slate-400">Same as above but for mobile devices</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.forceMinimizedMobile ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ forceMinimizedMobile: !workingStyleConfig.forceMinimizedMobile })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Fullscreen on mobile (legacy)</div>
                  <div className="text-xs text-slate-400">Older flag — prefer "Mobile fullscreen" below</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.fullscreenMobile ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ fullscreenMobile: !workingStyleConfig.fullscreenMobile })
                  }
                />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Mobile subsection */}
          <div>
            <div className="text-sm font-medium mb-3">Mobile</div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="pr-6">
                  <div className="text-sm font-normal">Mobile fullscreen</div>
                  <div className="text-xs text-slate-400">
                    On screens narrower than the breakpoint, the widget fills 100% of the viewport (ignores Window Width / Height).
                  </div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.mobileFullscreen ?? true}
                  changeToggle={() =>
                    updateStyleConfig({ mobileFullscreen: !(workingStyleConfig.mobileFullscreen ?? true) })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div className="pr-6">
                  <div className="text-sm font-normal">Large touch targets on mobile</div>
                  <div className="text-xs text-slate-400">
                    Bigger send button (min 56×56) and 16px input font (prevents iOS zoom). Recommended for finger use.
                  </div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.mobileLargeButtons ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ mobileLargeButtons: !(workingStyleConfig.mobileLargeButtons ?? false) })
                  }
                />
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-normal mb-1">Mobile breakpoint</div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      aria-label="Mobile breakpoint width in pixels"
                      className="border border-slate-300 rounded-md p-2 w-20 text-sm"
                      value={workingStyleConfig.mobileBreakpointPx ?? 640}
                      onChange={(e) =>
                        updateStyleConfig({
                          mobileBreakpointPx: clampedNumber(e, 320, 1024, 640)
                        })
                      }
                      min={320}
                      max={1024}
                    />
                    <span className="text-xs text-slate-400">px</span>
                  </div>
                </div>
                <div className="text-xs text-slate-400">
                  Viewport width below this threshold triggers mobile mode.
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
